import React, { useEffect, useRef, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Building2, MapPin, Fingerprint, Landmark, CheckCircle2, AlertCircle, Link2, Sparkles, Upload, FileSpreadsheet, Download, X, ChevronDown, ChevronUp, Pencil, Trash2, RotateCcw, FileArchive, Images, Eye, EyeOff, Globe } from "lucide-react";
import MasterProjectMediaPanel from "@/components/MasterProjectMediaPanel";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const MAHARERA_RE = /^P\d{8,13}$/;
const CONSTRUCTION_OPTIONS = [
  { value: "PRE_LAUNCH", label: "Pre-launch" },
  { value: "UNDER_CONSTRUCTION", label: "Under construction" },
  { value: "READY_TO_MOVE", label: "Ready to move" },
];

const EMPTY_FORM = {
  project_name: "",
  maharera_id: "",
  locality_tag: "",
  developer: "",
  starting_price_lakhs: "",
  configurations: "",
  construction_status: "PRE_LAUNCH",
  assigned_agent_slug: "",
};

// Sentinel used in the shadcn <Select> because a value of "" is treated as
// "no value" — we need a real string for the "no agent" option.
const NO_AGENT = "__none__";

export default function AdminIngestProject() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  // Displays the last retroactive-linking result in a persistent banner so
  // admins can see the details after the toast fades.
  const [lastUpgrade, setLastUpgrade] = useState(null);
  // Which recent-ingested cards have their media panel expanded.
  const [expandedMedia, setExpandedMedia] = useState({});
  // Edit-dialog state — { open, form }
  const [editing, setEditing] = useState(null);
  // Bulk CSV upload state
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkInputRef = useRef(null);

  // Bulk media ZIP state
  const [zipFile, setZipFile] = useState(null);
  const [zipDragOver, setZipDragOver] = useState(false);
  const [zipSubmitting, setZipSubmitting] = useState(false);
  const [zipResult, setZipResult] = useState(null);
  const zipInputRef = useRef(null);

  // Optional agent-assignment dropdown data
  const [agents, setAgents] = useState([]);

  // ─── RERA URL importer state ────────────────────────────────────
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importExtras, setImportExtras] = useState(null); // {project_address, city, total_units, carpet_area_range, possession_date, source_host}

  const loadRecent = () => {
    setLoadingRecent(true);
    api.get("/admin/ingested-projects")
      .then(({ data }) => setRecent(data.projects || []))
      .finally(() => setLoadingRecent(false));
  };

  useEffect(() => {
    api.get("/localities").then(({ data }) => setLocalities(data)).catch(() => {});
    api.get("/admin/agents")
      .then(({ data }) => setAgents((data.agents || []).filter((a) => a.slug)))
      .catch(() => {});
    loadRecent();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // ─── RERA URL importer handler ──────────────────────────────────
  // Fetches a SiteSetu / RERA aggregator page on the server, parses it,
  // and pre-fills the form. Never auto-submits — all fields remain editable.
  const handleImportFromUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setImporting(true);
    setImportError(null);
    setImportExtras(null);
    try {
      const { data } = await api.post("/master-projects/import-from-url", { url });
      if (!data?.ok) {
        setImportError(data?.error || "Could not fetch data — please fill in the details manually.");
        return;
      }
      const d = data.data || {};

      // Map the parsed locality string to one of our known locality-tag options.
      // If it doesn't match, we surface it in the extras panel so the user can
      // pick manually — better than silently losing the value.
      const localityValue = (d.locality || "").trim();
      const localityMatches = allLocalityOptions.find(
        (o) => o.value.toLowerCase() === localityValue.toLowerCase(),
      );

      setForm((f) => ({
        ...f,
        project_name: d.project_name || f.project_name,
        maharera_id: (d.maharera_id || f.maharera_id).toUpperCase(),
        developer: d.developer || f.developer,
        locality_tag: localityMatches ? localityMatches.value : f.locality_tag,
        construction_status: d.construction_status || f.construction_status,
      }));

      // Everything the form doesn't yet capture as a field lands in an
      // "additional details" panel so it's visible before save.
      const extras = {
        project_address: d.project_address || null,
        city: d.city || null,
        total_units: d.total_units || null,
        carpet_area_range: d.carpet_area_range || null,
        possession_date: d.possession_date || null,
        locality_unmatched: (!localityMatches && localityValue) ? localityValue : null,
        source_host: data.source_host || null,
      };
      const hasExtras = Object.values(extras).some((v) => v !== null && v !== undefined && v !== "");
      setImportExtras(hasExtras ? extras : null);

      toast.success("RERA data imported — review fields below before saving.");
    } catch (e) {
      setImportError(
        e?.response?.data?.detail ||
        "Could not fetch data — please fill in the details manually.",
      );
    } finally {
      setImporting(false);
    }
  };

  // Live client-side validation — mirrors the backend regex so users get instant feedback.
  const reraValue = form.maharera_id.trim().toUpperCase();
  const reraTouched = form.maharera_id.length > 0;
  const reraValid = MAHARERA_RE.test(reraValue);
  const nameValid = form.project_name.trim().length >= 3;
  const localityValid = !!form.locality_tag;
  const priceValid = !form.starting_price_lakhs || (Number(form.starting_price_lakhs) > 0 && Number(form.starting_price_lakhs) <= 100000);
  const canSubmit = nameValid && reraValid && localityValid && priceValid && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const configs = form.configurations
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post("/admin/ingest-project", {
        project_name: form.project_name.trim(),
        maharera_id: reraValue,
        locality_tag: form.locality_tag,
        developer: form.developer.trim() || null,
        starting_price_lakhs: form.starting_price_lakhs ? Number(form.starting_price_lakhs) : null,
        configurations: configs,
        construction_status: form.construction_status,
        assigned_agent_slug: form.assigned_agent_slug || null,
      });
      const upgraded = data.retroactive_link?.upgraded || 0;
      const byRera = data.retroactive_link?.matched_by?.rera || 0;
      const byName = data.retroactive_link?.matched_by?.name || 0;
      if (upgraded > 0) {
        toast.success(
          `Added ${data.project.project_id} — ${data.project.project_name} · ${data.project.locality_tag}`,
          {
            description: `Upgraded ${upgraded} existing listing${upgraded === 1 ? "" : "s"}${
              byRera && byName ? ` (${byRera} by RERA, ${byName} by name)`
              : byRera ? ` (matched by MahaRERA)` : ` (matched by project name)`
            }.`,
            duration: 6000,
          },
        );
      } else {
        toast.success(`Added ${data.project.project_id} — ${data.project.project_name} · ${data.project.locality_tag}`);
      }
      setForm(EMPTY_FORM);
      setLastUpgrade({ project_id: data.project.project_id, project_name: data.project.project_name, ...data.retroactive_link });
      loadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add project.");
    } finally {
      setSubmitting(false);
    }
  };

  const allLocalityOptions = [
    ...(localities["Pune West"] || []).map((l) => ({ label: `${l} (Pune West)`, value: l })),
    ...(localities["Pune East"] || []).map((l) => ({ label: `${l} (Pune East)`, value: l })),
  ];

  // ---- Bulk CSV upload ----
  const onBulkFile = (f) => {
    if (!f) return;
    const okExt = /\.(csv|xlsx|xls)$/i.test(f.name);
    if (!okExt) {
      toast.error("Only .csv, .xlsx or .xls files are supported");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setBulkFile(f);
    setBulkResult(null);
  };

  const onBulkSubmit = async () => {
    if (!bulkFile) return;
    setBulkSubmitting(true);
    const toastId = toast.loading(`Uploading ${bulkFile.name}…`);
    try {
      const form = new FormData();
      form.append("file", bulkFile);
      const { data } = await api.post("/admin/bulk-ingest", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      const upgraded = data.retroactive_link?.upgraded || 0;
      toast.success(
        `${data.successful} successful · ${data.failed} failed${upgraded ? ` · ${upgraded} listing${upgraded === 1 ? "" : "s"} auto-linked` : ""}`,
        { id: toastId, duration: 6000 },
      );
      // Reset the picked file (keep the result visible)
      setBulkFile(null);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
      loadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk upload failed.", { id: toastId });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const onDownloadTemplate = async () => {
    try {
      const resp = await api.get("/admin/bulk-ingest/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "master_projects_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  // ---- Bulk media ZIP upload ----
  const onZipFile = (f) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) return toast.error("Please upload a .zip archive");
    if (f.size > 100 * 1024 * 1024) return toast.error("ZIP too large (max 100 MB)");
    setZipFile(f);
    setZipResult(null);
  };

  const onZipSubmit = async () => {
    if (!zipFile) return;
    setZipSubmitting(true);
    const toastId = toast.loading(`Processing ${zipFile.name}…`);
    try {
      const form = new FormData();
      form.append("file", zipFile);
      const { data } = await api.post("/admin/bulk-media-zip", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setZipResult(data);
      toast.success(
        `${data.hero_set} hero · ${data.brochures_set} brochure · ${data.images_appended} appended · ${data.unmatched} unmatched`,
        { id: toastId, duration: 6000 },
      );
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = "";
      loadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "ZIP import failed.", { id: toastId });
    } finally {
      setZipSubmitting(false);
    }
  };

  // ---- Edit / soft-delete / restore ----
  const openEdit = (p) => {
    setEditing({
      project_id: p.project_id,
      project_name: p.project_name || "",
      developer: p.developer || "",
      locality_tag: p.locality_tag || "",
      starting_price_lakhs: p.starting_price_lakhs != null ? String(p.starting_price_lakhs) : "",
      configurations: (p.configurations || []).join(", "),
      construction_status: p.construction_status || "PRE_LAUNCH",
      // "" here means "portal-owned"; the Select renders the NO_AGENT sentinel.
      assigned_agent_slug: p.assigned_agent_slug || "",
      _original_assigned_agent_slug: p.assigned_agent_slug || "",
      saving: false,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditing((e) => ({ ...e, saving: true }));
    try {
      const payload = {
        project_name: editing.project_name.trim() || null,
        developer: editing.developer.trim() || null,
        locality_tag: editing.locality_tag || null,
        starting_price_lakhs: editing.starting_price_lakhs === "" ? null : Number(editing.starting_price_lakhs),
        configurations: editing.configurations.split(",").map((s) => s.trim()).filter(Boolean),
        construction_status: editing.construction_status,
      };
      // Only include assigned_agent_slug when it actually changed — the backend
      // treats `null` / omitted as "leave unchanged" and empty string as "clear".
      if (editing.assigned_agent_slug !== editing._original_assigned_agent_slug) {
        payload.assigned_agent_slug = editing.assigned_agent_slug; // "" = clear
      }
      const { data } = await api.patch(`/admin/master-project/${editing.project_id}`, payload);
      setRecent((prev) => prev.map((r) => (r.project_id === data.project.project_id ? data.project : r)));
      toast.success(`Saved · ${data.changed_fields.filter((k) => !k.startsWith("last_edited")).length} field(s) updated`);
      setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes");
      setEditing((e) => ({ ...e, saving: false }));
    }
  };

  const softDelete = async (p) => {
    if (!window.confirm(`Soft-delete "${p.project_name}"? It'll disappear from all public pages but stay in the DB.`)) return;
    try {
      await api.delete(`/admin/master-project/${p.project_id}`);
      setRecent((prev) => prev.map((r) => (r.project_id === p.project_id ? { ...r, is_active: false } : r)));
      toast.success(`${p.project_name} soft-deleted`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/admin/master-project/${p.project_id}/restore`);
      setRecent((prev) => prev.map((r) => (r.project_id === p.project_id ? { ...r, is_active: true } : r)));
      toast.success(`${p.project_name} restored`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore");
    }
  };

  const toggleHidden = async (p) => {
    const isHidden = p.status === "hidden";
    try {
      await api.post(`/admin/master-project/${p.project_id}/${isHidden ? "show" : "hide"}`);
      setRecent((prev) => prev.map((r) => (r.project_id === p.project_id ? { ...r, status: isHidden ? null : "hidden" } : r)));
      toast.success(isHidden ? `${p.project_name} is now visible again` : `${p.project_name} hidden from public + brokers`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update visibility");
    }
  };

  return (
    <DashboardShell>
      <div className="overline text-sg-gold-deep">MAHARERA · INGESTION PIPELINE</div>
      <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
        Add a new <span className="font-serif-italic text-sg-gold-deep">master project.</span>
      </h1>
      <p className="text-sg-mute mt-2 max-w-2xl">
        Registered projects surface immediately in the Market Opportunities tab for every agent covering the chosen locality —
        no redeploy, no seed edit. Admin-ingested rows are preserved across backend restarts.
      </p>

      {lastUpgrade && (
        <div
          className={`mt-6 rounded-2xl border p-4 flex items-start gap-3 ${
            lastUpgrade.upgraded > 0
              ? "bg-emerald-50/60 border-emerald-200"
              : "bg-sg-cream-soft border-sg-line"
          }`}
          data-testid="retroactive-link-banner"
        >
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            lastUpgrade.upgraded > 0 ? "bg-emerald-100 text-emerald-700" : "bg-white border border-sg-line text-sg-mute"
          }`}>
            {lastUpgrade.upgraded > 0 ? <Sparkles className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-sg-navy flex items-center gap-2 flex-wrap">
              {lastUpgrade.upgraded > 0 ? (
                <>
                  <span data-testid="retroactive-upgrade-count">{lastUpgrade.upgraded}</span>
                  <span>existing listing{lastUpgrade.upgraded === 1 ? "" : "s"} upgraded</span>
                </>
              ) : (
                <span>No existing listings needed a link.</span>
              )}
              <span className="text-xs text-sg-mute font-normal">
                for {lastUpgrade.project_id} · {lastUpgrade.project_name}
              </span>
            </div>
            {lastUpgrade.upgraded > 0 && (
              <div className="text-xs text-sg-mute mt-1 flex flex-wrap gap-x-4">
                <span>Matched by MahaRERA: <strong className="text-sg-navy font-mono">{lastUpgrade.matched_by?.rera || 0}</strong></span>
                <span>Matched by project name: <strong className="text-sg-navy font-mono">{lastUpgrade.matched_by?.name || 0}</strong></span>
                <span className="text-emerald-700 font-medium">price_source → master_projects</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setLastUpgrade(null)}
            className="text-sg-mute hover:text-sg-navy text-xs shrink-0"
            data-testid="retroactive-link-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8">
        {/* -------- Left column: Bulk CSV + Single-project form -------- */}
        <div className="space-y-6">
        {/* -------- Step 1: CSV bulk ingest -------- */}
        <div className="bg-white border border-sg-line rounded-3xl p-6" data-testid="bulk-upload-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-bold text-sg-gold-deep uppercase tracking-widest" data-testid="step-1-label">STEP 1</div>
              <div className="text-sm font-semibold text-sg-navy flex items-center gap-2 mt-0.5">
                <FileSpreadsheet className="w-4 h-4 text-sg-gold-deep" /> Upload CSV
              </div>
              <div className="text-xs text-sg-mute mt-0.5">Import many projects at once. Now supports Image URLs (pipe-separated) + Video URL columns.</div>
            </div>
            <button
              type="button"
              onClick={onDownloadTemplate}
              className="text-xs text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1.5 underline underline-offset-2"
              data-testid="bulk-template-download"
            >
              <Download className="w-3.5 h-3.5" /> Download CSV template
            </button>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setBulkDragOver(true); }}
            onDragLeave={() => setBulkDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setBulkDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onBulkFile(f);
            }}
            onClick={() => bulkInputRef.current?.click()}
            className={`mt-4 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              bulkDragOver ? "bg-sg-gold/10 border-sg-gold" : "bg-sg-cream-soft/60 border-sg-line hover:border-sg-gold/60"
            }`}
            data-testid="bulk-drop-zone"
          >
            <input
              ref={bulkInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => onBulkFile(e.target.files?.[0])}
              data-testid="bulk-file-input"
            />
            <div className="w-11 h-11 mx-auto rounded-full bg-white border border-sg-line flex items-center justify-center">
              <Upload className="w-5 h-5 text-sg-gold-deep" />
            </div>
            <div className="text-sm font-semibold text-sg-navy mt-3">
              {bulkFile ? bulkFile.name : "Drop CSV / XLSX here, or click to browse"}
            </div>
            <div className="text-[11px] text-sg-mute mt-1">
              Required columns: Project Name · MahaRERA ID · Builder · Locality · Zone · (optional) Starting Price, Configurations, Construction Status
            </div>
          </div>

          {bulkFile && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-sg-mute font-mono truncate flex-1">
                {bulkFile.name} · {(bulkFile.size / 1024).toFixed(1)} KB
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setBulkFile(null); if (bulkInputRef.current) bulkInputRef.current.value = ""; }}
                  className="text-xs text-sg-mute hover:text-sg-navy inline-flex items-center gap-1"
                  data-testid="bulk-clear"
                >
                  <X className="w-3 h-3" /> clear
                </button>
                <Button
                  type="button"
                  onClick={onBulkSubmit}
                  disabled={bulkSubmitting}
                  className="h-9 rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white px-4 disabled:opacity-50"
                  data-testid="bulk-submit"
                >
                  {bulkSubmitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-2" />}
                  {bulkSubmitting ? "Uploading…" : "Upload & ingest"}
                </Button>
              </div>
            </div>
          )}

          {bulkResult && (
            <div className="mt-5 rounded-2xl bg-sg-cream-soft border border-sg-line p-4" data-testid="bulk-result-panel">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-semibold" data-testid="bulk-result-success">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {bulkResult.successful} successful
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${bulkResult.failed ? "bg-red-100 border border-red-200 text-red-800" : "bg-white border border-sg-line text-sg-mute"}`} data-testid="bulk-result-failed">
                  <AlertCircle className="w-3.5 h-3.5" /> {bulkResult.failed} failed
                </span>
                {(bulkResult.retroactive_link?.upgraded || 0) > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sg-gold/15 border border-sg-gold/40 text-sg-gold-deep text-xs font-semibold" data-testid="bulk-result-linked">
                    <Sparkles className="w-3.5 h-3.5" /> {bulkResult.retroactive_link.upgraded} listing{bulkResult.retroactive_link.upgraded === 1 ? "" : "s"} auto-linked
                  </span>
                )}
                <span className="text-xs text-sg-mute ml-auto">{bulkResult.total_rows} row{bulkResult.total_rows === 1 ? "" : "s"} processed</span>
              </div>

              {bulkResult.failed > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-sg-navy mb-1">Failed rows</div>
                  <div className="max-h-40 overflow-auto rounded-lg border border-sg-line bg-white divide-y divide-sg-line" data-testid="bulk-failures-list">
                    {bulkResult.failures.map((f, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start gap-3">
                        <span className="font-mono text-sg-mute shrink-0">row {f.row}</span>
                        <span className="text-red-700">{f.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResult.retroactive_link?.per_project?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-sg-navy mb-1">Retroactive links per project</div>
                  <div className="rounded-lg border border-sg-line bg-white divide-y divide-sg-line">
                    {bulkResult.retroactive_link.per_project.map((p) => (
                      <div key={p.project_id} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                        <span className="truncate"><span className="font-mono text-sg-mute">{p.project_id}</span> · <span className="text-sg-navy">{p.project_name}</span></span>
                        <span className="text-sg-mute">
                          {p.upgraded} linked <span className="text-[10px]">(RERA:{p.matched_by?.rera || 0} · name:{p.matched_by?.name || 0})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* -------- Step 2: Media ZIP importer -------- */}
        <div className="bg-white border border-sg-line rounded-3xl p-6" data-testid="bulk-zip-panel">
          <div>
            <div className="text-[10px] font-bold text-sg-gold-deep uppercase tracking-widest" data-testid="step-2-label">STEP 2</div>
            <div className="text-sm font-semibold text-sg-navy flex items-center gap-2 mt-0.5">
              <FileArchive className="w-4 h-4 text-sg-gold-deep" /> Upload media ZIP
            </div>
            <div className="text-xs text-sg-mute mt-0.5">
              Zip images + brochures, name them like <code className="font-mono px-1 rounded bg-sg-cream-soft">P52100012345_hero.jpg</code>, <code className="font-mono px-1 rounded bg-sg-cream-soft">P52100012345_brochure.pdf</code>, or <code className="font-mono px-1 rounded bg-sg-cream-soft">P52100012345_amenity.jpg</code>. We route them to the right project automatically.
            </div>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setZipDragOver(true); }}
            onDragLeave={() => setZipDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setZipDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) onZipFile(f);
            }}
            onClick={() => zipInputRef.current?.click()}
            className={`mt-4 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              zipDragOver ? "bg-sg-gold/10 border-sg-gold" : "bg-sg-cream-soft/60 border-sg-line hover:border-sg-gold/60"
            }`}
            data-testid="zip-drop-zone"
          >
            <input
              ref={zipInputRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => onZipFile(e.target.files?.[0])}
              data-testid="zip-file-input"
            />
            <div className="w-11 h-11 mx-auto rounded-full bg-white border border-sg-line flex items-center justify-center">
              <FileArchive className="w-5 h-5 text-sg-gold-deep" />
            </div>
            <div className="text-sm font-semibold text-sg-navy mt-3">
              {zipFile ? zipFile.name : "Drop ZIP here, or click to browse"}
            </div>
            <div className="text-[11px] text-sg-mute mt-1">
              ZIP up to 100 MB · 500 files max · images (PNG/JPG/WEBP) + brochure PDFs
            </div>
          </div>

          {zipFile && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-xs text-sg-mute font-mono truncate flex-1">
                {zipFile.name} · {(zipFile.size / 1024).toFixed(1)} KB
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setZipFile(null); if (zipInputRef.current) zipInputRef.current.value = ""; }}
                  className="text-xs text-sg-mute hover:text-sg-navy inline-flex items-center gap-1"
                  data-testid="zip-clear"
                >
                  <X className="w-3 h-3" /> clear
                </button>
                <Button
                  type="button"
                  onClick={onZipSubmit}
                  disabled={zipSubmitting}
                  className="h-9 rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white px-4 disabled:opacity-50"
                  data-testid="zip-submit"
                >
                  {zipSubmitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-2" />}
                  {zipSubmitting ? "Processing…" : "Import ZIP"}
                </Button>
              </div>
            </div>
          )}

          {zipResult && (
            <div className="mt-5 rounded-2xl bg-sg-cream-soft border border-sg-line p-4" data-testid="zip-result-panel">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-semibold" data-testid="zip-result-matched">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {zipResult.matched} project{zipResult.matched === 1 ? "" : "s"} updated
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sg-gold/15 border border-sg-gold/40 text-sg-gold-deep text-xs font-semibold" data-testid="zip-result-hero">
                  <Sparkles className="w-3.5 h-3.5" /> {zipResult.hero_set} hero
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sg-gold/15 border border-sg-gold/40 text-sg-gold-deep text-xs font-semibold" data-testid="zip-result-brochures">
                  <FileArchive className="w-3.5 h-3.5" /> {zipResult.brochures_set} brochure
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sg-gold/15 border border-sg-gold/40 text-sg-gold-deep text-xs font-semibold" data-testid="zip-result-images">
                  <Images className="w-3.5 h-3.5" /> {zipResult.images_appended} images appended
                </span>
                {zipResult.unmatched > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-xs font-semibold" data-testid="zip-result-unmatched">
                    <AlertCircle className="w-3.5 h-3.5" /> {zipResult.unmatched} unmatched
                  </span>
                )}
                <span className="text-xs text-sg-mute ml-auto">{zipResult.total_files} file{zipResult.total_files === 1 ? "" : "s"} processed</span>
              </div>

              {zipResult.per_project?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-sg-navy mb-1">Per project</div>
                  <div className="rounded-lg border border-sg-line bg-white divide-y divide-sg-line" data-testid="zip-per-project-list">
                    {zipResult.per_project.map((p) => (
                      <div key={p.project_id} className="px-3 py-2 text-xs">
                        <div className="font-mono text-sg-mute">{p.project_id} · <span className="text-sg-navy">{p.project_name}</span></div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                          {p.hero_set && <span>hero: <span className="font-mono text-sg-navy">{p.hero_set}</span></span>}
                          {p.brochure_set && <span>brochure: <span className="font-mono text-sg-navy">{p.brochure_set}</span></span>}
                          {p.images_appended?.length > 0 && (
                            <span>appended: <span className="font-mono text-sg-navy">{p.images_appended.length}</span></span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {zipResult.unmatched_files?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-sg-navy mb-1">Unmatched files</div>
                  <div className="max-h-40 overflow-auto rounded-lg border border-sg-line bg-white divide-y divide-sg-line" data-testid="zip-unmatched-list">
                    {zipResult.unmatched_files.map((u, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start gap-3">
                        <span className="font-mono text-sg-mute shrink-0 max-w-[220px] truncate">{u.filename}</span>
                        <span className="text-amber-800">{u.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* -------- Single-project form -------- */}
        <form onSubmit={onSubmit} className="bg-white border border-sg-line rounded-3xl p-6 space-y-5" data-testid="admin-ingest-form">
          {/* ═══ Import from RERA URL ═══ */}
          <div
            className="rounded-2xl border border-sg-gold/40 bg-sg-gold/5 p-4 md:p-5"
            data-testid="rera-import-panel"
          >
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-sg-gold-deep" />
              <Label htmlFor="rera-import-url" className="text-xs font-semibold text-sg-navy uppercase tracking-wide">
                Import from RERA URL
              </Label>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-sg-mute font-mono">
                SiteSetu · MahaRERA
              </span>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                id="rera-import-url"
                type="url"
                value={importUrl}
                onChange={(e) => { setImportUrl(e.target.value); setImportError(null); }}
                placeholder="https://sitesetu.app/rera/maharashtra/pune/..."
                className="flex-1 rounded-xl bg-white border-sg-line"
                data-testid="rera-import-url"
                disabled={importing}
              />
              <Button
                type="button"
                onClick={handleImportFromUrl}
                disabled={importing || !importUrl.trim()}
                className="rounded-xl bg-sg-navy hover:bg-sg-navy-700 text-white whitespace-nowrap"
                data-testid="rera-import-btn"
              >
                {importing
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching…</>
                  : <><Download className="w-4 h-4 mr-2" /> Import &amp; auto-fill</>}
              </Button>
            </div>
            <div className="text-xs text-sg-mute mt-2">
              Paste a public RERA-listing URL. We&apos;ll fetch it, extract project details, and pre-fill the form below — all fields stay editable before you save.
            </div>

            {importError && (
              <div
                className="mt-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs flex items-start gap-2"
                data-testid="rera-import-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{importError}</span>
              </div>
            )}

            {importExtras && (
              <div
                className="mt-3 rounded-xl border border-sg-line bg-white px-3 py-2.5"
                data-testid="rera-import-extras"
              >
                <div className="text-[10px] uppercase tracking-widest text-sg-gold-deep font-semibold mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Additional details parsed{importExtras.source_host ? ` from ${importExtras.source_host}` : ""}
                </div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {importExtras.project_address && (
                    <div className="flex gap-2"><dt className="text-sg-mute w-32 flex-shrink-0">Address</dt><dd className="text-sg-navy">{importExtras.project_address}</dd></div>
                  )}
                  {importExtras.city && (
                    <div className="flex gap-2"><dt className="text-sg-mute w-32 flex-shrink-0">City</dt><dd className="text-sg-navy">{importExtras.city}</dd></div>
                  )}
                  {importExtras.total_units && (
                    <div className="flex gap-2"><dt className="text-sg-mute w-32 flex-shrink-0">Total Units</dt><dd className="text-sg-navy">{importExtras.total_units}</dd></div>
                  )}
                  {importExtras.carpet_area_range && (
                    <div className="flex gap-2"><dt className="text-sg-mute w-32 flex-shrink-0">Carpet Area</dt><dd className="text-sg-navy">{importExtras.carpet_area_range}</dd></div>
                  )}
                  {importExtras.possession_date && (
                    <div className="flex gap-2"><dt className="text-sg-mute w-32 flex-shrink-0">Possession</dt><dd className="text-sg-navy">{importExtras.possession_date}</dd></div>
                  )}
                  {importExtras.locality_unmatched && (
                    <div className="flex gap-2 col-span-full"><dt className="text-amber-700 w-32 flex-shrink-0">Locality (unmatched)</dt><dd className="text-amber-900">{importExtras.locality_unmatched} — please pick from the dropdown below.</dd></div>
                  )}
                </dl>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="project_name" className="text-xs font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5" /> Project name
            </Label>
            <Input
              id="project_name"
              value={form.project_name}
              onChange={(e) => setField("project_name", e.target.value)}
              placeholder="e.g. Godrej Skyline"
              className="mt-2 rounded-xl bg-white border-sg-line"
              data-testid="ingest-project-name"
              autoFocus
            />
            {form.project_name && !nameValid && (
              <div className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Must be at least 3 characters</div>
            )}
          </div>

          <div>
            <Label htmlFor="maharera_id" className="text-xs font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-2">
              <Fingerprint className="w-3.5 h-3.5" /> MahaRERA ID
            </Label>
            <Input
              id="maharera_id"
              value={form.maharera_id}
              onChange={(e) => setField("maharera_id", e.target.value.toUpperCase())}
              placeholder="P52100012345"
              className={`mt-2 rounded-xl bg-white font-mono uppercase ${reraTouched && !reraValid ? "border-red-300 focus-visible:ring-red-200" : "border-sg-line"}`}
              maxLength={14}
              data-testid="ingest-rera-id"
              aria-invalid={reraTouched && !reraValid}
            />
            <div className="text-xs mt-1 flex items-center gap-1" data-testid="rera-validation-hint">
              {reraTouched && reraValid && <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Valid format</span>}
              {reraTouched && !reraValid && <span className="text-red-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Must match <code className="font-mono px-1 rounded bg-red-50">^P\d&#123;8,13&#125;$</code></span>}
              {!reraTouched && <span className="text-sg-mute">Pattern: <code className="font-mono px-1 rounded bg-sg-cream-soft">^P\d&#123;8,13&#125;$</code> — 8 to 13 digits after P</span>}
            </div>
          </div>

          <div>
            <Label htmlFor="locality" className="text-xs font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Locality
            </Label>
            <Select value={form.locality_tag} onValueChange={(v) => setField("locality_tag", v)}>
              <SelectTrigger id="locality" className="mt-2 rounded-xl bg-white border-sg-line" data-testid="ingest-locality-select">
                <SelectValue placeholder="Choose a Pune micro-market" />
              </SelectTrigger>
              <SelectContent>
                {allLocalityOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`ingest-locality-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-sg-mute mt-1">Determines which brokers see this project in their Master Projects tab.</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="developer" className="text-xs font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-2">
                <Landmark className="w-3.5 h-3.5" /> Developer <span className="text-sg-mute font-normal normal-case">(optional)</span>
              </Label>
              <Input
                id="developer"
                value={form.developer}
                onChange={(e) => setField("developer", e.target.value)}
                placeholder="e.g. Godrej Properties"
                className="mt-2 rounded-xl bg-white border-sg-line"
                data-testid="ingest-developer"
              />
            </div>

            <div>
              <Label htmlFor="starting_price" className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
                Starting price <span className="text-sg-mute font-normal normal-case">(₹ lakhs, optional)</span>
              </Label>
              <Input
                id="starting_price"
                type="number"
                min="0"
                step="1"
                value={form.starting_price_lakhs}
                onChange={(e) => setField("starting_price_lakhs", e.target.value)}
                placeholder="e.g. 115"
                className="mt-2 rounded-xl bg-white border-sg-line"
                data-testid="ingest-starting-price"
              />
              {!priceValid && (
                <div className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Must be between 1 and 100000</div>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="configurations" className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
              Configurations <span className="text-sg-mute font-normal normal-case">(comma-separated, optional)</span>
            </Label>
            <Textarea
              id="configurations"
              value={form.configurations}
              onChange={(e) => setField("configurations", e.target.value)}
              placeholder="e.g. 2 BHK, 3 BHK, 3 BHK + Study"
              rows={2}
              className="mt-2 rounded-xl bg-white border-sg-line resize-none"
              data-testid="ingest-configurations"
            />
          </div>

          <div>
            <Label htmlFor="construction_status" className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
              Construction status
            </Label>
            <Select value={form.construction_status} onValueChange={(v) => setField("construction_status", v)}>
              <SelectTrigger id="construction_status" className="mt-2 rounded-xl bg-white border-sg-line" data-testid="ingest-construction-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONSTRUCTION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="assigned_agent_slug" className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
              Assigned Agent <span className="text-sg-mute font-normal normal-case">(optional — surfaces on the /buy card as &quot;Contact Authorized Agent&quot;)</span>
            </Label>
            <Select
              value={form.assigned_agent_slug || NO_AGENT}
              onValueChange={(v) => setField("assigned_agent_slug", v === NO_AGENT ? "" : v)}
            >
              <SelectTrigger
                id="assigned_agent_slug"
                className="mt-2 rounded-xl bg-white border-sg-line"
                data-testid="ingest-assigned-agent"
              >
                <SelectValue placeholder="Portal-owned (default)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_AGENT}>Portal-owned (INPropPortal · Verified Launch)</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.slug} value={a.slug}>
                    {a.name || a.email} · /agent/{a.slug}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="pt-2 flex items-center justify-between gap-3 border-t border-sg-line">
            <div className="text-xs text-sg-mute inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-sg-gold-deep" /> Ingested projects survive backend restarts.
            </div>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="rounded-full h-11 px-6 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50"
              data-testid="ingest-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {submitting ? "Adding…" : "Add project"}
            </Button>
          </div>
        </form>
        </div>{/* /left column */}

        {/* -------- Recently ingested -------- */}
        <div className="bg-sg-cream-soft border border-sg-line rounded-3xl p-6" data-testid="recent-ingested-panel">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-sg-gold-deep">RECENTLY INGESTED</div>
              <div className="text-sg-mute text-xs mt-1">Admin-added projects · newest first</div>
            </div>
            <Badge className="rounded-full bg-white border border-sg-line text-sg-navy font-mono">{recent.length}</Badge>
          </div>

          <div className="mt-5 space-y-3 max-h-[560px] overflow-auto pr-1">
            {loadingRecent && (
              <div className="text-sg-mute inline-flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            )}
            {!loadingRecent && recent.length === 0 && (
              <div className="text-sm text-sg-mute py-10 text-center border border-dashed border-sg-line rounded-2xl bg-white" data-testid="recent-ingested-empty">
                No admin-ingested projects yet. Your first entry will appear here.
              </div>
            )}
            {recent.map((p) => {
              const mediaOpen = !!expandedMedia[p.project_id];
              const mediaCount = (p.image_urls?.length || 0) + (p.video_url ? 1 : 0) + (p.brochure_url ? 1 : 0);
              const heroUrl = p.image_urls?.[0];
              const isInactive = p.is_active === false;
              return (
              <div
                key={p.project_id}
                className={`bg-white border rounded-2xl p-4 ${isInactive ? "border-red-200 bg-red-50/30 opacity-80" : "border-sg-line"}`}
                data-testid={`recent-ingested-${p.project_id}`}
              >
                {isInactive && (
                  <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 border border-red-200 text-red-700 text-[10px] font-semibold uppercase tracking-wide" data-testid={`inactive-badge-${p.project_id}`}>
                    <Trash2 className="w-3 h-3" /> Soft-deleted
                  </div>
                )}
                <div className="flex items-start gap-3">
                  {heroUrl && (
                    <img
                      src={heroUrl}
                      alt={p.project_name}
                      className="w-14 h-14 rounded-lg object-cover border border-sg-line flex-shrink-0"
                      data-testid={`recent-hero-thumb-${p.project_id}`}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-sg-navy truncate">{p.project_name}</div>
                    <div className="text-xs text-sg-mute font-mono mt-0.5">{p.project_id} · {p.maharera_id}</div>
                  </div>
                  <Badge className="rounded-full bg-sg-gold/15 text-sg-gold-deep border border-sg-gold/40 whitespace-nowrap">{p.zone}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-sg-mute">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.locality_tag}</span>
                  {p.developer && <span>· {p.developer}</span>}
                  {p.starting_price_lakhs != null && <span>· ₹{p.starting_price_lakhs.toLocaleString("en-IN")} L onwards</span>}
                  <span>· {p.construction_status?.replace(/_/g, " ").toLowerCase()}</span>
                </div>
                {p.ingested_at && (
                  <div className="text-[10px] text-sg-mute font-mono mt-2">
                    Ingested {new Date(p.ingested_at).toLocaleString()}
                  </div>
                )}

                {/* Assigned Agent chip — clickable, opens edit dialog focused on assignment. */}
                <button
                  type="button"
                  onClick={() => openEdit(p)}
                  className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                    p.assigned_agent_slug
                      ? "bg-sg-gold/10 border-sg-gold/40 text-sg-navy hover:bg-sg-gold/20"
                      : "bg-sg-cream-soft border-sg-line text-sg-mute hover:border-sg-gold/40 hover:text-sg-navy"
                  }`}
                  data-testid={`assigned-agent-chip-${p.project_id}`}
                  title={p.assigned_agent_slug ? "Click to change assignment" : "Click to assign an agent"}
                >
                  {p.assigned_agent_slug ? (
                    <>
                      <span className="text-sg-gold-deep">Assigned:</span>
                      <span>{p.assigned_agent_slug}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sg-mute">Portal-owned ·</span>
                      <span className="text-sg-gold-deep">Assign agent →</span>
                    </>
                  )}
                </button>

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setExpandedMedia((s) => ({ ...s, [p.project_id]: !s[p.project_id] }))}
                    className="text-xs text-sg-gold-deep hover:text-sg-navy inline-flex items-center gap-1"
                    data-testid={`media-toggle-${p.project_id}`}
                  >
                    {mediaOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {mediaOpen ? "Hide media" : "Manage media"}
                    {mediaCount > 0 && !mediaOpen && (
                      <span className="ml-1 text-[10px] font-mono text-sg-mute">({mediaCount} attached)</span>
                    )}
                  </button>
                  <span className="text-sg-line">·</span>
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    className="text-xs text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1"
                    data-testid={`edit-btn-${p.project_id}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  {isInactive ? (
                    <button
                      type="button"
                      onClick={() => restore(p)}
                      className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                      data-testid={`restore-btn-${p.project_id}`}
                    >
                      <RotateCcw className="w-3 h-3" /> Restore
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleHidden(p)}
                        className="text-xs text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1"
                        data-testid={`hide-btn-${p.project_id}`}
                      >
                        {p.status === "hidden" ? (
                          <><Eye className="w-3 h-3" /> Show</>
                        ) : (
                          <><EyeOff className="w-3 h-3" /> Hide</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => softDelete(p)}
                        className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                        data-testid={`delete-btn-${p.project_id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </>
                  )}
                </div>
                {mediaOpen && (
                  <MasterProjectMediaPanel
                    project={p}
                    onChange={(updated) => setRecent((prev) => prev.map((r) => (r.project_id === updated.project_id ? updated : r)))}
                  />
                )}
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* -------- Edit dialog -------- */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg bg-white p-6 rounded-2xl" data-testid="edit-project-dialog">
          {editing && (
            <>
              <div className="overline text-sg-gold-deep">EDIT PROJECT</div>
              <h2 className="font-display text-2xl text-sg-navy mt-1">
                {editing.project_id}
              </h2>
              <p className="text-xs text-sg-mute mt-1">MahaRERA ID is not editable — delete + re-ingest to change it.</p>

              <div className="mt-4 space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Project name</Label>
                  <Input
                    value={editing.project_name}
                    onChange={(e) => setEditing((s) => ({ ...s, project_name: e.target.value }))}
                    className="mt-1 rounded-xl bg-white border-sg-line"
                    data-testid="edit-project-name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Developer</Label>
                  <Input
                    value={editing.developer}
                    onChange={(e) => setEditing((s) => ({ ...s, developer: e.target.value }))}
                    className="mt-1 rounded-xl bg-white border-sg-line"
                    data-testid="edit-developer"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Locality</Label>
                  <Select value={editing.locality_tag} onValueChange={(v) => setEditing((s) => ({ ...s, locality_tag: v }))}>
                    <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="edit-locality">
                      <SelectValue placeholder="Choose locality" />
                    </SelectTrigger>
                    <SelectContent>
                      {allLocalityOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`edit-locality-${o.value}`}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Price (₹ lakhs)</Label>
                    <Input
                      type="number"
                      value={editing.starting_price_lakhs}
                      onChange={(e) => setEditing((s) => ({ ...s, starting_price_lakhs: e.target.value }))}
                      className="mt-1 rounded-xl bg-white border-sg-line"
                      data-testid="edit-price"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Construction status</Label>
                    <Select value={editing.construction_status} onValueChange={(v) => setEditing((s) => ({ ...s, construction_status: v }))}>
                      <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="edit-construction-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONSTRUCTION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">
                    Assigned Agent <span className="normal-case font-normal text-sg-mute">(controls the &quot;Contact Authorized Agent&quot; strip on /buy)</span>
                  </Label>
                  <Select
                    value={editing.assigned_agent_slug || NO_AGENT}
                    onValueChange={(v) => setEditing((s) => ({ ...s, assigned_agent_slug: v === NO_AGENT ? "" : v }))}
                  >
                    <SelectTrigger className="mt-1 rounded-xl bg-white border-sg-line" data-testid="edit-assigned-agent">
                      <SelectValue placeholder="Portal-owned (default)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_AGENT}>Portal-owned (INPropPortal · Verified Launch)</SelectItem>
                      {agents.map((a) => (
                        <SelectItem key={a.slug} value={a.slug} data-testid={`edit-assigned-agent-${a.slug}`}>
                          {a.name || a.email} · /agent/{a.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-sg-mute uppercase tracking-wide">Configurations <span className="text-sg-mute font-normal normal-case">(comma-separated)</span></Label>
                  <Textarea
                    value={editing.configurations}
                    onChange={(e) => setEditing((s) => ({ ...s, configurations: e.target.value }))}
                    rows={2}
                    className="mt-1 rounded-xl bg-white border-sg-line resize-none"
                    data-testid="edit-configurations"
                  />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="text-sm text-sg-mute hover:text-sg-navy px-4 py-2"
                  data-testid="edit-cancel"
                >
                  Cancel
                </button>
                <Button
                  type="button"
                  onClick={saveEdit}
                  disabled={editing.saving}
                  className="rounded-full h-10 px-5 bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50"
                  data-testid="edit-save"
                >
                  {editing.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editing.saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
